import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, type Currency, type Language } from "@nh/shared";
import { cn } from "@/shared/lib/cn";
import { useLanguage } from "@/shared/i18n/useLanguage";

/** La bandera del país de cada moneda. Dato, no JSX. */
const CURRENCY_FLAGS: Record<Currency, string> = {
  MXN: "🇲🇽",
  USD: "🇺🇸",
};

const LANGUAGE_LABELS: Record<Language, string> = {
  es: "ES",
  en: "EN",
};

const PILL =
  "h-8 rounded-ds-pill border border-ds-border-strong bg-white py-0 text-[12px] font-semibold text-ds-ink-3";

/**
 * Los dos mandos de la barra superior, desde el 14-sep-2026 (Anahí):
 *
 *  · La MONEDA no se elige: la decide el país de conexión
 *    (`useShopCurrency`). Aquí solo se DICE, con su bandera, para que de
 *    un vistazo se sepa en qué están los precios. Es un `<span>`, no un
 *    botón: un control que no hace nada parece roto.
 *  · El IDIOMA sí se elige, y no toca los precios. Un `<select>` nativo
 *    vestido de píldora, por las mismas razones que tenía el selector de
 *    moneda que sustituye: teclado, foco y la rueda del sistema en
 *    móvil sin reimplementar nada.
 */
export function LocaleControls({ currency, className }: { currency: Currency; className?: string }) {
  const { t } = useTranslation();
  const [language, setLanguage] = useLanguage();

  return (
    <div className={cn("flex shrink-0 items-center gap-1.5", className)}>
      <span
        className={cn(PILL, "inline-flex items-center px-3")}
        title={t("Precios en {{currency}}, según el país desde el que te conectas", { currency })}
      >
        <span aria-hidden="true">{CURRENCY_FLAGS[currency]}</span>
        <span className="ml-1.5">{currency}</span>
        <span className="sr-only">{t("Moneda")}</span>
      </span>

      <div className="relative">
        <select
          value={language}
          onChange={(event) => setLanguage(event.target.value as Language)}
          aria-label={t("Idioma")}
          className={cn(
            PILL,
            "cursor-pointer appearance-none pl-3 pr-7 outline-none transition",
            "hover:border-ds-violet-pop hover:text-ds-violet-pop",
            "focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ds-cream",
          )}
        >
          {LANGUAGES.map((option) => (
            <option key={option} value={option}>
              {LANGUAGE_LABELS[option]}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-2.5 my-auto h-3.5 w-3.5 text-ds-ink-3"
        />
      </div>
    </div>
  );
}
